// Generated by ReScript, PLEASE EDIT WITH CARE

import * as React from "react";
import * as Stdlib_Array from "@rescript/runtime/lib/es6/Stdlib_Array.js";
import * as Stdlib_Option from "@rescript/runtime/lib/es6/Stdlib_Option.js";
import * as Primitive_object from "@rescript/runtime/lib/es6/Primitive_object.js";
import * as Primitive_option from "@rescript/runtime/lib/es6/Primitive_option.js";

function combineEffects(effects) {
  return t => {
    let cleanupFunctions = Stdlib_Array.filterMap(effects, effect => effect(t));
    if (cleanupFunctions.length !== 0) {
      return () => {
        cleanupFunctions.forEach(cleanup => cleanup());
      };
    }
  };
}

function ignoreCancel(effect, t) {
  effect(t);
}

function noEffect(param) {
  
}

function decomposeInit(init) {
  if (init.TAG === "Init") {
    return [
      init._0,
      []
    ];
  } else {
    return [
      init._0,
      [init._1]
    ];
  }
}

function decomposeUpdate(update, previousState) {
  if (typeof update !== "object") {
    return [
      previousState,
      []
    ];
  }
  switch (update.TAG) {
    case "Update" :
      return [
        update._0,
        []
      ];
    case "UpdateWithEffect" :
      return [
        update._0,
        [update._1]
      ];
    case "Effect" :
      return [
        previousState,
        [update._0]
      ];
  }
}

function initToUpdate(init) {
  if (init.TAG === "Init") {
    return {
      TAG: "Update",
      _0: init._0
    };
  } else {
    return {
      TAG: "UpdateWithEffect",
      _0: init._0,
      _1: init._1
    };
  }
}

function optionalUpdate(mbUpdatedState) {
  if (mbUpdatedState !== undefined) {
    return {
      TAG: "Update",
      _0: Primitive_option.valFromOption(mbUpdatedState)
    };
  } else {
    return "NoUpdate";
  }
}

function useReducerWithDependencies(dependencies, initState, dependenciesEqualOpt, handleDependenciesChangedOpt, logDebugWithTag, handleAction) {
  let dependenciesEqual = dependenciesEqualOpt !== undefined ? dependenciesEqualOpt : Primitive_object.equal;
  let handleDependenciesChanged = handleDependenciesChangedOpt !== undefined ? handleDependenciesChangedOpt : (param, param$1) => "Reinit";
  let cleanupEffects = React.useRef([]);
  let runCleanupEffects = () => {
    cleanupEffects.current.forEach(cleanup => cleanup());
    cleanupEffects.current = [];
  };
  let match = React.useReducer((fullState, fullAction) => {
    let logDebugWithTag = fullState.logDebugWithTag;
    let queuedEffects = fullState.queuedEffects;
    let applicationState = fullState.applicationState;
    let applicationDependencies = fullState.applicationDependencies;
    if (fullAction.TAG === "ApplicationAction") {
      let action = fullAction._0;
      let update = handleAction({
        dependencies: applicationDependencies,
        state: applicationState,
        action: action
      });
      let match = decomposeUpdate(update, applicationState);
      let effects = match[1];
      let state = match[0];
      Stdlib_Option.forEach(logDebugWithTag, __x => {
        console.log(__x, "Action received", action, [
          "state",
          applicationState,
          state
        ], [
          "effects",
          effects
        ]);
      });
      return {
        applicationDependencies: fullState.applicationDependencies,
        applicationState: state,
        initialEffects: fullState.initialEffects,
        queuedEffects: {
          contents: queuedEffects.contents.concat(effects)
        },
        logDebugWithTag: fullState.logDebugWithTag
      };
    }
    let dependencies = fullAction._0;
    if (dependenciesEqual(dependencies, applicationDependencies)) {
      return fullState;
    }
    let reinit = handleDependenciesChanged(applicationDependencies, dependencies);
    let match$1;
    if (typeof reinit !== "object") {
      match$1 = [
        decomposeInit(initState(dependencies)),
        true
      ];
    } else if (reinit.TAG === "ReinitAndMergeState") {
      let match$2 = decomposeInit(initState(dependencies));
      match$1 = [
        [
          reinit._0(applicationState, match$2[0]),
          match$2[1]
        ],
        true
      ];
    } else {
      match$1 = [
        decomposeUpdate(handleAction({
          dependencies: dependencies,
          state: applicationState,
          action: reinit._0
        }), applicationState),
        false
      ];
    }
    let match$3 = match$1[0];
    let effects$1 = match$3[1];
    let state$1 = match$3[0];
    Stdlib_Option.forEach(logDebugWithTag, __x => {
      console.log(__x, "Dependencies changed", [
        "dependencies",
        applicationDependencies,
        dependencies
      ], [
        "state",
        applicationState,
        state$1
      ], [
        "effects",
        effects$1
      ]);
    });
    if (match$1[1]) {
      Stdlib_Option.forEach(logDebugWithTag, __x => {
        console.log(__x, "Running effects cleanup on reinit");
      });
      runCleanupEffects();
    }
    return {
      applicationDependencies: dependencies,
      applicationState: state$1,
      initialEffects: fullState.initialEffects,
      queuedEffects: {
        contents: queuedEffects.contents.concat(effects$1)
      },
      logDebugWithTag: fullState.logDebugWithTag
    };
  }, undefined, () => {
    let init = initState(dependencies);
    Stdlib_Option.forEach(logDebugWithTag, __x => {
      console.log(__x, "Initialized", init);
    });
    let match = decomposeInit(init);
    return {
      applicationDependencies: dependencies,
      applicationState: match[0],
      initialEffects: match[1],
      queuedEffects: {
        contents: []
      },
      logDebugWithTag: logDebugWithTag
    };
  });
  let dispatch = match[1];
  let match$1 = match[0];
  let queuedEffects = match$1.queuedEffects;
  let initialEffects = match$1.initialEffects;
  React.useEffect(() => {
    dispatch({
      TAG: "DependenciesChanged",
      _0: dependencies
    });
  }, [dependencies]);
  let applicationDispatch = React.useCallback(action => dispatch({
    TAG: "ApplicationAction",
    _0: action
  }), [dispatch]);
  let reducer_dependencies = match$1.applicationDependencies;
  let reducer_state = match$1.applicationState;
  let reducer = {
    dependencies: reducer_dependencies,
    state: reducer_state,
    dispatch: applicationDispatch
  };
  React.useEffect(() => {
    let cleanupFunctions = Stdlib_Array.filterMap(initialEffects, effect => effect(reducer));
    cleanupEffects.current.push(...cleanupFunctions);
  }, []);
  React.useEffect(() => {
    if (queuedEffects.contents.length !== 0) {
      let effectsToRun = queuedEffects.contents.slice();
      queuedEffects.contents = [];
      let cleanupFunctions = Stdlib_Array.filterMap(effectsToRun, effect => effect(reducer));
      cleanupEffects.current.push(...cleanupFunctions);
    }
  }, [queuedEffects]);
  React.useEffect(() => {
    Stdlib_Option.forEach(logDebugWithTag, __x => {
      console.log(__x, "Component mounted");
    });
    return () => {
      Stdlib_Option.forEach(logDebugWithTag, __x => {
        console.log(__x, "Component unmounted; cleaning up effects");
      });
      runCleanupEffects();
    };
  }, []);
  return reducer;
}

function useReducer(initState, logDebugWithTag, handleAction) {
  let match = useReducerWithDependencies(undefined, initState, undefined, undefined, logDebugWithTag, param => handleAction(param.state, param.action));
  return [
    match.state,
    match.dispatch
  ];
}

export {
  combineEffects,
  ignoreCancel,
  noEffect,
  decomposeInit,
  decomposeUpdate,
  initToUpdate,
  optionalUpdate,
  useReducerWithDependencies,
  useReducer,
}
/* react Not a pure module */
